import { SelectedMetadataFields } from '@modules/ManageUnifiedFields/types';
import { mapMetaDataTypeToEQLFieldValueType } from '@modules/ManageUnifiedFields/utils';
import { TypographyWithEllipsis } from '@modules/common/components/TypographyWithEllipsis';
import { CircleRounded, CloseRounded } from '@mui/icons-material';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { EqlFieldValueType, MetadataDataType } from 'generated/graphql';
import { MetadataSampleValue } from '../MetadataSampleValue';
import { SelectedMetadataFieldsListEmptyState } from './SelectedMetadataFieldsListEmptyState';

interface SelectedMetadataFieldsListProps {
  selectedMetadataField: SelectedMetadataFields;
  removeSelectedMetadata: (metadata: SelectedMetadataFields[number]) => void;
  metadataType?: MetadataDataType;
}

export const SelectedMetadataFieldsList = ({
  selectedMetadataField,
  removeSelectedMetadata,
  metadataType,
}: SelectedMetadataFieldsListProps) => {
  const renderPrimaryText = (label: string) => {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Tooltip title={label} placement="bottom-start">
          <TypographyWithEllipsis width="85%">{label}</TypographyWithEllipsis>
        </Tooltip>
      </Stack>
    );
  };

  const renderSecondaryText = (
    metadataField: string,
    type: EqlFieldValueType,
    source: string,
    sourceIconURL?: string,
  ) => {
    return (
      <Stack spacing={2} direction="row" alignItems="center" mt={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            component="img"
            width="16px"
            height="auto"
            src={sourceIconURL}
            alt={`${source}-icon`}
            loading="lazy"
          />
          <Typography variant="caption">{source}</Typography>
        </Stack>
        <Stack width="100%" direction="row" alignItems="center" spacing={1}>
          <CircleRounded sx={{ width: 10 }} />
          <Box width="80%">
            <MetadataSampleValue metadataKey={metadataField} />
          </Box>
        </Stack>
      </Stack>
    );
  };

  return (
    <Stack mx={1}>
      {selectedMetadataField.length > 0 ? (
        <List>
          {selectedMetadataField.map((metadata, index) => (
            <>
              <ListItem
                disablePadding
                key={metadata.field}
                alignItems="flex-start"
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => removeSelectedMetadata(metadata)}
                  >
                    <CloseRounded />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={renderPrimaryText(metadata.label)}
                  secondary={renderSecondaryText(
                    metadata.field,
                    metadata.type ??
                      mapMetaDataTypeToEQLFieldValueType(metadataType),
                    metadata.source,
                    metadata.sourceIconURL,
                  )}
                />
              </ListItem>
              {index < selectedMetadataField.length - 1 && (
                <Divider variant="fullWidth" component="li" />
              )}
            </>
          ))}
        </List>
      ) : (
        <SelectedMetadataFieldsListEmptyState />
      )}
    </Stack>
  );
};
